import React, { useState, useRef, useEffect } from "react";
import "./index.scss";

/**
 * DeptMultiSelect — standalone multi-select checkbox dropdown for AA Department.
 * Uses position:fixed so the dropdown always renders above modal overlays.
 */
const DeptMultiSelect = ({
  options = [],
  selected = [],
  onToggle,
  placeholder = "Select departments",
  error = false,
  disabled = false,
  label,
  required = false,
  errorMsg,
}) => {
  const [open, setOpen] = useState(false);
  const [dropPos, setDropPos] = useState({ top: 0, left: 0, width: 0 });
  const triggerRef = useRef(null);
  const dropRef = useRef(null);
  const wrapRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (
        wrapRef.current &&
        !wrapRef.current.contains(e.target) &&
        dropRef.current &&
        !dropRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Reposition on open
  const handleToggleOpen = () => {
    if (disabled) return;
    if (!open && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setDropPos({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    }
    setOpen((v) => !v);
  };

  // Reposition on scroll/resize while open
  useEffect(() => {
    if (!open) return;
    const reposition = () => {
      if (triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        setDropPos({
          top: rect.bottom + 4,
          left: rect.left,
          width: rect.width,
        });
      }
    };
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [open]);

  // Strip blank placeholder option
  const filteredOptions = options.filter((o) => o.value !== "");

  // Resolve labels for selected values
  const selectedLabels = selected
    .map((v) => {
      const opt = filteredOptions.find(
        (o) => o.value?.toLowerCase() === v?.toLowerCase(),
      );
      return { value: v, label: opt?.label || v };
    })
    .filter((x) => x.label);

  return (
    <div className="dms-wrap" ref={wrapRef}>
      {/* Label */}
      {label && (
        <label className="dms-label">
          {label}
          {required && <span className="dms-required">*</span>}
        </label>
      )}

      <div
        className={[
          "dms",
          error ? "dms--error" : "",
          disabled ? "dms--disabled" : "",
          open ? "dms--open" : "",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        {/* ── Trigger ──────────────────────────────────────────────────── */}
        <button
          ref={triggerRef}
          type="button"
          className="dms__trigger"
          onClick={handleToggleOpen}
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          {selectedLabels.length === 0 ? (
            <span className="dms__placeholder">{placeholder}</span>
          ) : (
            <div className="dms__tags">
              {selectedLabels.map(({ value, label: lbl }) => (
                <span key={value} className="dms__tag">
                  {lbl}
                  {!disabled && (
                    <button
                      type="button"
                      className="dms__tag-remove"
                      aria-label={`Remove ${lbl}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        onToggle(value);
                      }}
                    >
                      ×
                    </button>
                  )}
                </span>
              ))}
            </div>
          )}
          <svg
            className={`dms__arrow${open ? " dms__arrow--up" : ""}`}
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>
      </div>

      {/* ── Dropdown — rendered via fixed position above modal ─────────── */}
      {open && (
        <div
          ref={dropRef}
          className="dms__dropdown"
          role="listbox"
          aria-multiselectable="true"
          style={{
            position: "fixed",
            top: dropPos.top,
            left: dropPos.left,
            width: dropPos.width,
            zIndex: 9999,
          }}
        >
          {filteredOptions.length === 0 ? (
            <div className="dms__empty">
              No departments available for this LOB
            </div>
          ) : (
            <>
              {filteredOptions.map((opt) => {
                const checked = selected.some(
                  (s) => s?.toLowerCase() === opt.value?.toLowerCase(),
                );
                return (
                  <label
                    key={opt.value}
                    className={`dms__option${checked ? " dms__option--checked" : ""}`}
                    role="option"
                    aria-selected={checked}
                  >
                    <input
                      type="checkbox"
                      className="dms__checkbox"
                      checked={checked}
                      onChange={() => onToggle(opt.value)}
                    />
                    <span className="dms__option-label">{opt.label}</span>
                    {checked && (
                      <svg
                        className="dms__tick"
                        width="13"
                        height="13"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    )}
                  </label>
                );
              })}

              {/* Footer */}
              {selectedLabels.length > 0 && (
                <div className="dms__footer">
                  <span className="dms__count">
                    {selectedLabels.length} selected
                  </span>
                  <button
                    type="button"
                    className="dms__clear"
                    onClick={(e) => {
                      e.stopPropagation();
                      selected.forEach((v) => onToggle(v));
                    }}
                  >
                    Clear all
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Error message */}
      {errorMsg && <p className="dms-error-msg">{errorMsg}</p>}
    </div>
  );
};

export default DeptMultiSelect;
